<script setup>
  import { computed, onMounted, ref, toRaw, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import UserModalSummary from './UserModalSummary.vue';
  import UserIdentityForm from './UserIdentityForm.vue';
  import UserModalTabs from './UserModalTabs.vue';
  import SearchableList from '../components/SearchableList.vue';
  import { fetchAllLibrariesOrRetailersByNetwork, fetchAllLibrariesOrRetailers } from '../services/LibrariesRetailersService';
  import { saveUser, updateUser, getUserLibraryNetwork } from '../services/UsersService';
  import { BSpinner } from 'bootstrap-vue-next';

  const emits = defineEmits(['closeModal', 'showToast']);
  const { t } = useI18n();
  const i18nScope = ref('components.users_management');

  const props = defineProps({
    userType: {
      type: String,
      required: true,
      default: 'user'
    },
    userRoles: {
      type: Array,
      required: true,
      default: () => []
    },
    defaultRole: {
      type: Object,
      required: false,
      default: () => ({})
    },
    networks: {
      type: Array,
      required: true,
      default: () => []
    },
    defaultNetwork: {
      type: Object,
      required: false,
      default: () => ({})
    },
    networkAdmin: {
      type: Boolean,
      required: false,
      default: false
    },
    libraryOptions: {
      type: Array,
      required: true,
      default: () => []
    },
    defaultLibrary: {
      type: Object,
      required: false,
      default: () => ({})
    },
    libraryAdmin : {
      type: Boolean,
      required: false,
      default: false
    },
    defaultRetailer: {
      type: Object,
      required: false,
      default: () => ({})
    },
    retailerAdmin: {
      type: Boolean,
      required: false,
      default: false
    },
    selectedUser: {
      type: Object,
      required: false,
      default: () => ({})
    }
  });

  const userDataPristine = ref({
    id: null,
    username: null,
    role: {
      id: null,
      name: null
    },
    network: {
      id: null,
      name: null
    },
    library: {
      id: null,
      loading: false,
      name: null
    },
    retailer: {
      id: null,
      loading: false,
      name: null
    },
    firstName: null,
    lastName: null,
    fullName: null,
    mail: null,

    //permissions
    manageNetworks: false,
    manageLibraries: false,
    manageRetailers: false,
    manageActivateCarts: false,
    manageLibraryAdmins: false,
    manageCuration: false,
    manageOrders: false,
    manageRetailerAdmins: false
  });

  const step = ref(1);
  const librariesOptions = ref([]);
  const retailersOptions = ref([]);
  const userData = ref(toRaw(userDataPristine.value));
  const saving = ref(false);
  const loading = ref(false);
  const userIsRequired = ref(false);
  const identityTouched = ref(false);
  const firstNameIsRequired = computed(() => identityTouched.value && !isFirstOrLastNameValid(true));
  const lastNameIsRequired = computed(() => identityTouched.value && !isFirstOrLastNameValid(false));
  const emailIsRequired = computed(() => identityTouched.value && !isEmailValid());
  const networkIsRequired = ref(false);
  const libraryIsRequired = ref(false);
  const retailerIsRequired = ref(false);
  const userRoles = ref([...props.userRoles]);

  const tabs = ref([
    {
      name: 'role',
      label: 'Role Selection',
      rolesAllowed: ['admin', 'support', 'finance', 'platform_manager', 'library_manager', 'retailer_manager'],
      step: 1
    },
    {
      name: 'network_selection',
      label: 'Network Selection',
      rolesAllowed: ['platform_manager', 'library_manager', 'retailer_manager'],
      step: 2
    },
    {
      name: 'library_selection',
      label: 'Library Selection',
      rolesAllowed: ['library_manager'],
      step: 3
    },
    {
      name: 'retailer_selection',
      label: 'Retailer Selection',
      rolesAllowed: ['retailer_manager'],
      step: 4
    },
    {
      name: 'identity',
      label: 'Identity',
      rolesAllowed: ['admin', 'support', 'finance', 'platform_manager', 'library_manager', 'retailer_manager'],
      step: 5
    },
    {
      name: 'summary',
      label: 'Summary',
      rolesAllowed: ['admin', 'support', 'finance', 'platform_manager', 'library_manager', 'retailer_manager'],
      step: 6
    }
  ]);

  const tabAdvance = (e) => {
    // Do not go from step 3 to 4, is the firstName, lastName or email are not valid
    if (step.value === 5 && !isIdentityValid()) {
      identityTouched.value = true;
      return;
    }

    // Network is mandatory for Platform Manager only (optional for Library Manager and Retailer Manager)
    if (step.value === 2 && !userData.value.network.id && (
       userData.value.role?.id === 'platform_manager'
      )) {
      networkIsRequired.value = true;
      return;
    } else {
      networkIsRequired.value = false;
    }

    // Library is mandatory for Library Manager
    if (step.value === 3 && userData.value.role?.id === 'library_manager' && !userData.value.library.id) {
      libraryIsRequired.value = true;
      return;
    } else {
      libraryIsRequired.value = false;
    }

    // Retailer is mandatory for Retailer Manager
    if (step.value === 4 && userData.value.role?.id === 'retailer_manager' && !userData.value.retailer.id) {
      retailerIsRequired.value = true;
      return;
    } else {
      retailerIsRequired.value = false;
    }

    calcStep(true);
    showTab(step.value);
  };

  const tabBack = () => {
    calcStep(false);
  };

  const calcStep = (forward = true) => {
    let nextStep = step.value + (forward ? 1 : -1);
    const direction = forward ? 1 : -1;

    while (nextStep >= 1 && nextStep <= tabs.value.length) {
      const tab = tabs.value[nextStep - 1];
      if (tab.rolesAllowed.includes(userData.value.role?.id)) {
        step.value = nextStep;
        showTab(step.value);
        return;
      }
      nextStep += direction;
    }
  };

  // Helper function to show tab by step
  const showTab = (stepNumber) => {
    const tabTrigger = document.getElementById(`${tabs.value[stepNumber - 1].name}-tab`);
    tabTrigger && window.bootstrap.Tab.getOrCreateInstance(tabTrigger).show();
  };

  const advanceLibraryTab = (selected) => {
    userData.value.library = selected;
    userData.value.library.name = selected.name;
    calcStep(true);
    showTab(step.value);
  };

  const handleLibraryChange = (selected) => {
    userData.value.library = selected;
    if (selected?.id) {
      advanceLibraryTab(selected);
    }
  };

  const advanceRetailerTab = (selected) => {
    userData.value.retailer = selected;
    userData.value.retailer.name = selected.name;
    calcStep(true);
    showTab(step.value);
  };

  const handleRetailerChange = (selected) => {
    userData.value.retailer = selected;
    if (selected?.id) {
      advanceRetailerTab(selected);
    }
  };

  // FBKS-2834: a brand-new library_manager defaulted to "Activer les paniers"
  // unchecked, silently creating admins without cart-validation rights (the
  // legacy, pre-flag default every prior admin had). Mirror "Gérer les
  // gestionnaires de bibliothèque" into it for NEW users only -- editing an
  // existing user already prefills the real current value from the backend.
  watch(() => userData.value.manageLibraryAdmins, (isAdmin) => {
    if (props.selectedUser?.id) return;
    userData.value.manageActivateCarts = isAdmin;
  });

  const showCreateButton = computed(() => {
    return step.value === tabs.value.length && !props.selectedUser.id;
  });

  const showUpdateButton = computed(() => {
    return step.value === tabs.value.length && props.selectedUser.id;
  });

  const showBackButton = computed(() => {
    return step.value > 1 && props.userType === 'admin';
  });

  const resetAndCancel = () => {
    userData.value = toRaw(userDataPristine.value);
    emits('closeModal');
  };

  const isIdentityValid = () => {
    return isFirstOrLastNameValid(true)
      && isFirstOrLastNameValid(false)
      && isEmailValid();
  };

  const isFirstOrLastNameValid = (first = true) => {
    return first ? userData.value.firstName && userData.value.firstName.length >= 3 ? true : false
                 : userData.value.lastName && userData.value.lastName.length >= 3 ? true : false;
  };

  // Validate email as Rails does
  const isEmailValid = () => {
    return userData.value.mail && /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(userData.value.mail) ? true : false;
  };

  const asyncFind = async (clearSelection = false) => {

    let type = null;
    if (userData.value.role?.id === 'library_manager') {
      type = 'libraries';
    } else if (userData.value.role?.id === 'retailer_manager') {
      type = 'retailers';
    }

    if (!type) return;

    // Set loading state and optionally clear selection
    if (type === 'libraries') {
      const currentLibrary = userData.value.library;
      userData.value.library.loading = true;
      librariesOptions.value = [];

      if (clearSelection) {
        userData.value.library = { id: null, name: '', loading: true };
      } else {
        userData.value.library = { ...currentLibrary, loading: true };
      }
    } else {
      const currentRetailer = userData.value.retailer;
      userData.value.retailer.loading = true;
      retailersOptions.value = [];

      if (clearSelection) {
        userData.value.retailer = { id: null, name: '', loading: true };
      } else {
        userData.value.retailer = { ...currentRetailer, loading: true };
      }
    }

    let results = null;
    if (
      (userData.value.role?.id === 'library_manager' || userData.value.role?.id === 'retailer_manager') &&
      !userData.value.network?.id
    ) {
      results = await fetchAllLibrariesOrRetailers(type);
    } else if (userData.value.network?.id) {
      results = await fetchAllLibrariesOrRetailersByNetwork(type, userData.value.network.id);
    }

    if (type === 'libraries') {
      librariesOptions.value = results || [];
      userData.value.library.loading = false;
    } else {
      retailersOptions.value = results || [];
      userData.value.retailer.loading = false;
    }
  };

  const insertOrUpdateUser = async (insert = true) => {
    // Re-check correct tab
    if (!isIdentityValid()) {
      identityTouched.value = true;
      step.value = 5;
      showTab(step.value);
      return;
    }

    saving.value = true;
    // Checkbox -> payload key mapping; see Api::UsersController for how each
    // key maps to a model attribute server-side.
    const user = {
      mail: userData.value.mail,
      first_name: userData.value.firstName,
      last_name: userData.value.lastName,
      role: userData.value.role?.id,
      network_id: userData.value.network?.id,
      library_id: userData.value.library?.id,
      retailer_id: userData.value.retailer?.id,
      manage_network: userData.value.manageNetworks,
      manage_library: userData.value.manageLibraries,
      manage_retailer: userData.value.manageRetailers,
      manage_activate_carts: userData.value.manageActivateCarts,
      manage_curation: userData.value.manageCuration,
      manage_orders: userData.value.manageOrders,
      manage_library_admins: userData.value.manageLibraryAdmins,
      manage_retailer_admins: userData.value.manageRetailerAdmins
    };

    const resp = insert ? await saveUser(user) : await updateUser(props.selectedUser.id, user);

    if ('user' in resp) {
      emits('showToast', {
        type: 'success',
        message: insert ? 'user_created_success' : 'user_updated_success'
      });
      resetAndCancel();
    } else {
      emits('showToast', {
        type: 'error',
        message: insert ? 'user_created_error' : 'user_updated_error'
      });
      resetAndCancel();
    }
  };

  onMounted(async () => {
    loading.value = true;

    if (props.selectedUser && props.selectedUser.id) {

      // This is an existing user, let's set up some initial data
      // FBKS-2843: pass the library currently selected in the management
      // table's filter, so the modal reflects that library's permissions --
      // not an arbitrary one of the user's other libraries.
      const data = await getUserLibraryNetwork(props.selectedUser.id, props.defaultLibrary?.id);
      userData.value = {
        ...toRaw(userDataPristine.value),
        id: props.selectedUser.id,
        username: props.selectedUser.username,
        firstName: props.selectedUser.firstName || '',
        lastName: props.selectedUser.lastName || '',
        mail: props.selectedUser.mail || '',
        role: {
          id: props.selectedUser.role.id || null,
          name: props.selectedUser.role.name || ''
        },
        network: {
          id: data.defaultNetworkId || null,
          name: data.defaultNetworkName || '',
          loading: false
        },
        library: {
          id: data.defaultLibraryId || null,
          name: data.defaultLibraryName || '',
          loading: false
        },
        retailer: {
          id: data.defaultRetailerId || null,
          name: data.defaultRetailerName || '',
          loading: false
        },
        manageNetworks: data.manageNetworks || false,
        manageLibraries: data.manageLibraries || false,
        manageRetailers: data.manageRetailers || false,
        manageCuration: data.manageCuration || false,
        manageLibraryAdmins: data.manageLibraryAdmins || false,
        manageActivateCarts: data.manageActivateCarts || false,
        manageOrders: data.manageOrders || false,
        manageRetailerAdmins: data.manageRetailerAdmins || false
      };

      step.value = 5; // Go to the identity step
      loading.value = false;
    } else {
      setDefaultValues();

      // Fetch libraries/retailers if we have a network and role
      if (userData.value.network?.id && userData.value.role?.id) {
        await asyncFind();
      } else if (userData.value.role?.id === 'library_manager' || userData.value.role?.id === 'retailer_manager') {
        await asyncFind();
      }

      loading.value = false;
    }
  });

  const computedPrivileges = computed(() => {
    let manageActivateCarts = false;
    let manageNetworks = false;
    let manageLibraries = false;
    let manageCuration = false;
    let manageRetailers = false;
    let manageOrders = false;
    let manageLibraryAdmins = false;
    let manageRetailerAdmins = false;

    if (userData.value.role.id === 'platform_manager') {
      manageNetworks = true;
      manageLibraries = true;
      manageRetailers = true;
      manageCuration = true;
    } else if (userData.value.role.id === 'library_manager') { // Gestionnaire de bibliothèque
      manageActivateCarts = true;
      manageLibraryAdmins = true;
    } else if (userData.value.role.id === 'retailer_manager') { // Libraire
      manageOrders = true;
      manageRetailerAdmins = true;
    }

    return {
      manageActivateCarts,
      manageNetworks,
      manageLibraries,
      manageCuration,
      manageRetailers,
      manageOrders,
      manageLibraryAdmins,
      manageRetailerAdmins
    };
  });

  const setDefaultValues = () => {
    let startingStep = 0;
    userData.value = toRaw(userDataPristine.value);
    userData.value.network = { ...userDataPristine.value.network, ...toRaw(props.defaultNetwork) };
    userData.value.library = { ...userDataPristine.value.library, ...toRaw(props.defaultLibrary) };
    userData.value.retailer = { ...userDataPristine.value.retailer, ...toRaw(props.defaultRetailer) };
    userData.value.role = { ...userDataPristine.value.role, ...toRaw(props.defaultRole) };

    // Determine which step to skip to, based on predefined values
    if (userData.value.role?.id) {

      if (userData.value.role.id === 'library_manager') {
        // A pre-selected library should skip ahead on its own, regardless of network.
        if (userData.value.library?.id) {
          startingStep = 3; // Library already selected -- skip straight past it
        } else if (userData.value.network?.id) {
          startingStep = 2; // Network exists but library doesn't
        } else {
          startingStep = 1;
        }
        step.value = startingStep;
        calcStep(true);
        showTab(step.value);
        return;
      }

      if (userData.value.role.id === 'retailer_manager') {
        if (userData.value.retailer?.id) {
          startingStep = 4; // Retailer already selected -- skip straight past it
        } else if (userData.value.network?.id) {
          startingStep = 3; // Network exists but retailer doesn't
        } else {
          startingStep = 1;
        }
        step.value = startingStep;
        calcStep(true);
        showTab(step.value);
        return;
      }

      if (userData.value.network?.id) {
        if (userData.value.library?.id) {
          startingStep = 3; // Go to identity step when all required fields are present
        } else {
          startingStep = 2; // Go to library selection when network exists but library doesn't
        }
      } else {
        startingStep = 1; // Go to network selection when role exists but network doesn't
      }
    } else {
      startingStep = 0; // Start at role selection when no role is set
    }

    step.value = startingStep;
    calcStep(true);
    showTab(step.value);
  };

  const clearLibraryOrRetailer = (type) => {
    if (type === 'library') {
      librariesOptions.value = [];
      userData.value.library = { id: null, name: null, loading: false };
    } else if (type === 'retailer') {
      retailersOptions.value = [];
      userData.value.retailer = { id: null, name: null, loading: false };
    }
  };

</script>

<template>
  <form class="container" v-if="!loading">
    <UserModalTabs
      :step="step"
      :tabsMeta="tabs"
      :currentRoleId="userData.role?.id"
      :t="t"
      :i18nScope="i18nScope"
      @tab-click="(newStep) => { step = newStep }"
    />
    <div class="tab-content" id="myTabContent">
      <div class="row align-items-start p-4">
        <div
          class="tab-pane fade"
          :class="{ 'show active': step === 1 }"
          id="role_page"
          role="tabpanel"
          aria-labelledby="role-tab"
          v-if="step === 1"
        >
          <h4>{{ t(`${i18nScope}.select_a_role`) }}</h4>
            <select
              class="form-select"
              aria-label="User's role"
              id="role_selection"
              :value="userData.role.id"
              @change="e => {
                const selected = userRoles.find(r => r.id == e.target.value);
                userData.role.id = selected ? selected.id : '';
                userData.role.name = selected ? selected.name : '';
                asyncFind();
                tabAdvance(e);
              }"
              required
            >
              <option disabled value="">{{ t(`${i18nScope}.select_a_role`) }}</option>
              <option v-for="role in userRoles" :value="role.id" :key="role.id">
                {{ role.name }}
              </option>
            </select>
          <div v-if="userIsRequired">
            <small class="text-danger">{{ t(`${i18nScope}.select_a_role_required`) }}</small>
          </div>
        </div>

        <div
          class="tab-pane fade"
          :class="{ 'show active': step === 2 }"
          id="network_page"
          role="tabpanel"
          aria-labelledby="network_selection-tab"
          v-if="step === 2"
        >
          <h4>{{ t(`${i18nScope}.select_a_network`) }}</h4>
          <select
            class="form-select"
            aria-label="User's network"
            id="network_selection"
            :value="userData.network.id"
            @change="e => {
              const selected = props.networks.find(n => n.id == e.target.value);
              userData.network.id = selected ? selected.id : '';
              userData.network.name = selected ? selected.name : '';
              asyncFind(true); // Clear library/retailer when network changes
              tabAdvance(e);
            }"
          >
            <option disabled value="">{{ t(`${i18nScope}.select_a_network`) }}</option>
            <option v-for="network in props.networks" :value="network.id">
              {{ network.name }}
            </option>
          </select>
          <div v-if="networkIsRequired">
            <small class="text-danger">{{ t(`${i18nScope}.select_a_network_required`) }}</small>
          </div>
        </div>

        <div
          class="tab-pane fade"
          :class="{ 'show active': step === 3 }"
          id="library_page"
          role="tabpanel"
          aria-labelledby="library_selection-tab"
          v-if="step === 3"
        >
          <label for="library" class="form-label">{{ t(`${i18nScope}.filters.library`) }}</label>
          <SearchableList
            v-model="userData.library"
            :options="librariesOptions"
            :disabled="false"
            :loading="userData.library.loading"
            :placeholder="t(`${i18nScope}.select_a_library`)"
            aria-label="Library"
            :clear-button-label="t(`${i18nScope}.clear_library_selection`)"
            @change="handleLibraryChange"
          />
          <div v-if="libraryIsRequired">
            <small class="text-danger">{{ t(`${i18nScope}.select_a_library_required`) }}</small>
          </div>
        </div>

        <div
          class="tab-pane fade"
          :class="{ 'show active': step === 4 }"
          id="retailer_page"
          role="tabpanel"
          aria-labelledby="retailer-tab"
          v-if="step === 4"
        >
          <label for="retailer" class="form-label">{{ t(`${i18nScope}.filters.retailer`) }}</label>
          <SearchableList
            v-model="userData.retailer"
            :options="retailersOptions"
            :disabled="false"
            :loading="userData.retailer.loading"
            :placeholder="t(`${i18nScope}.select_a_retailer`)"
            aria-label="Retailer"
            :clear-button-label="t(`${i18nScope}.clear_retailer_selection`)"
            @change="handleRetailerChange"
          />
          <div v-if="retailerIsRequired">
            <small>{{ t(`${i18nScope}.make_sure_to_select_a_retailer_or_ignored`) }}</small>
          </div>
        </div>

        <div
          class="tab-pane fade"
          :class="{ 'show active': step === 5 }"
          id="identity_page"
          role="tabpanel"
          aria-labelledby="identity-tab"
          v-if="step === 5"
        >
          <UserIdentityForm
            :userData="userData"
            :retailersOptions="retailersOptions"
            :computedPrivileges="computedPrivileges"
            :t="t"
            :i18nScope="i18nScope"
            :firstNameIsRequired="firstNameIsRequired"
            :lastNameIsRequired="lastNameIsRequired"
            :emailIsRequired="emailIsRequired"
          />
        </div>

        <div
          class="tab-pane fade"
          :class="{ 'show active': step === 6 }"
          id="summary_page"
          role="tabpanel"
          aria-labelledby="summary-tab"
          v-if="step === 6"
        >
          <UserModalSummary :userData="userData" :computedPrivileges="computedPrivileges" />
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-center" v-if="step !== 6">
      <ul>
        <li v-if="userData.role.name">
          <strong>{{ t(`${i18nScope}.user_details.role`) }}:</strong> {{ userData.role.name }}
        </li>
        <li v-if="userData.network.name">
          <strong>{{ t(`${i18nScope}.user_details.network`) }}:</strong> {{ userData.network.name }}
        </li>
        <li v-if="userData.library.name">
          <strong>{{ t(`${i18nScope}.user_details.library`) }}:</strong> {{ userData.library.name }}
        </li>
        <li v-if="userData.retailer.name">
          <strong>{{ t(`${i18nScope}.user_details.retailer`) }}:</strong> {{ userData.retailer.name }}
        </li>
      </ul>
    </div>

    <div class="model-buttons" v-if="!saving">
      <div class="container">
        <div class="row">

          <div class="col">
            <div class="d-flex gap-2 justify-content-start">
              &nbsp;
            </div>
          </div>

          <div class="col">
            <div class="d-flex gap-2 justify-content-end">
              <button type="button" class="btn btn-outline-secondary" @click.prevent="tabBack" v-if="showBackButton">
                {{ t(`${i18nScope}.user_details.go_back`) }}
              </button>
              <button type="button" class="btn btn-outline-secondary" @click.prevent="tabAdvance" v-if="step < tabs.length">
                {{ t(`${i18nScope}.user_details.next`) }}
              </button>
              <button type="button" class="btn btn-primary" v-if="showCreateButton" @click.prevent="insertOrUpdateUser(true)">
                {{ t(`${i18nScope}.user_details.create`) }}
              </button>
              <button type="button" class="btn btn-primary" v-if="showUpdateButton" @click.prevent="insertOrUpdateUser(false)">
                {{ t(`${i18nScope}.update_user`) }}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  </form>

  <div class="d-flex justify-content-center align-items-center">
    <BSpinner style="width: 3rem; height: 3rem;" variant="primary" v-if="loading || saving" />
  </div>
</template>

<style scoped lang="scss">
  :deep() {
    @import '../../../node_modules/bootstrap/scss/bootstrap.scss';
    @import '../../../node_modules/bootstrap-vue-next/dist/bootstrap-vue-next';
  }
</style>
